import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const leagueLabels = {
  la_liga: 'La Liga',
  premier_league: 'Premier League',
  bundesliga: 'Bundesliga',
  serie_a: 'Serie A',
  ligue_1: 'Ligue 1',
  champions_league: 'Champions League',
  mls: 'MLS',
  liga_mx: 'Liga MX',
};

// Progress Bar component for analysis
const ProgressBar = ({ value, color = 'bg-[#00E599]', label }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-zinc-400 w-10">{label}</span>
    <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${value}%` }} />
    </div>
    <span className="text-xs font-medium text-white w-8 text-right">{value}%</span>
  </div>
);

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [league, setLeague] = useState('all');
  const [days, setDays] = useState('7');
  const [analyzingEvent, setAnalyzingEvent] = useState(null);
  const [eventAnalysis, setEventAnalysis] = useState({});

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/events/all?days=${days}`;
      
      if (league !== 'all') {
        url = `${API_URL}/api/events?league=${league}&days=${days}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
        setEvents([]);
      } else {
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [league, days]);

  const handleAnalyzeEvent = async (event) => {
    const eventKey = `${event.home_team}_${event.away_team}`;
    
    if (eventAnalysis[eventKey]) {
      setEventAnalysis(prev => {
        const copy = {...prev};
        delete copy[eventKey];
        return copy;
      });
      return;
    }
    
    setAnalyzingEvent(eventKey);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/events/full-analysis`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          home_team: event.home_team,
          away_team: event.away_team,
          match_date: event.match_date,
          league: event.league_key || '',
          venue: event.venue || '',
          event_id: event.id || event.event_id || ''
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setEventAnalysis(prev => ({...prev, [eventKey]: data}));
      } else {
        toast.error('Error al analizar');
      }
    } catch (error) {
      console.error('Error analyzing event:', error);
      toast.error('Error al analizar');
    } finally {
      setAnalyzingEvent(null);
    }
  };

  const filteredEvents = events.filter(event => {
    if (league !== 'all' && event.league_key !== league) return false;
    return true;
  });

  const groupedEvents = filteredEvents.reduce((groups, event) => {
    const date = new Date(event.match_date).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      <main className="pt-16 pb-20 px-3 sm:px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header - Mobile optimized */}
          <div className="flex items-center justify-between gap-2 mb-4 pt-2">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                EVENTOS
              </h1>
              <p className="text-xs sm:text-sm text-zinc-400">Próximos {days} días</p>
            </div>
            <Button 
              onClick={fetchEvents} 
              variant="outline" 
              size="sm"
              className="border-zinc-700 h-9"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualizar'}
            </Button>
          </div>

          {/* Filters - Mobile optimized */}
          <div className="flex gap-2 mb-4">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="flex-1 sm:w-[120px] sm:flex-none bg-zinc-900 border-zinc-800 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="1">Hoy</SelectItem>
                <SelectItem value="3">3 días</SelectItem>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="14">14 días</SelectItem>
              </SelectContent>
            </Select>

            <Select value={league} onValueChange={setLeague}>
              <SelectTrigger className="flex-1 sm:w-[160px] sm:flex-none bg-zinc-900 border-zinc-800 h-10">
                <SelectValue placeholder="Liga" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="la_liga">La Liga</SelectItem>
                <SelectItem value="premier_league">Premier</SelectItem>
                <SelectItem value="bundesliga">Bundesliga</SelectItem>
                <SelectItem value="serie_a">Serie A</SelectItem>
                <SelectItem value="ligue_1">Ligue 1</SelectItem>
                <SelectItem value="champions_league">Champions</SelectItem>
                <SelectItem value="mls">MLS</SelectItem>
                <SelectItem value="liga_mx">Liga MX</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-[#F43F5E]/10 border border-[#F43F5E]/20 rounded-xl p-4 mb-4">
              <p className="text-sm text-zinc-300">{error}</p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#00E599] animate-spin" />
            </div>
          )}

          {/* Empty */}
          {!loading && !error && filteredEvents.length === 0 && (
            <div className="text-center py-16">
              <p className="text-zinc-400">Sin eventos</p>
            </div>
          )}

          {/* Events List - Mobile optimized */}
          {!loading && Object.keys(groupedEvents).length > 0 && (
            <div className="space-y-6">
              {Object.entries(groupedEvents).map(([date, dateEvents]) => (
                <div key={date}>
                  <h2 className="font-heading text-sm font-bold text-[#00E599] mb-3 uppercase tracking-wide">
                    {date}
                  </h2>
                  <div className="space-y-3">
                    {dateEvents.map((event) => {
                      const eventKey = `${event.home_team}_${event.away_team}`;
                      const analysis = eventAnalysis[eventKey];
                      const isAnalyzing = analyzingEvent === eventKey;
                      
                      return (
                        <div
                          key={event.id}
                          className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
                        >
                          {/* Event Card - Mobile layout */}
                          <div className="p-3 sm:p-4">
                            {/* Top row: Teams + Time */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  {event.home_logo && (
                                    <img src={event.home_logo} alt="" className="w-5 h-5 sm:w-6 sm:h-6 object-contain flex-shrink-0" />
                                  )}
                                  <span className="font-medium text-white text-sm sm:text-base truncate">{event.home_team}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {event.away_logo && (
                                    <img src={event.away_logo} alt="" className="w-5 h-5 sm:w-6 sm:h-6 object-contain flex-shrink-0" />
                                  )}
                                  <span className="font-medium text-white text-sm sm:text-base truncate">{event.away_team}</span>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-xs mb-1">
                                  {leagueLabels[event.league_key]?.slice(0,8) || 'Liga'}
                                </Badge>
                                <p className="text-xs text-zinc-500">
                                  {new Date(event.match_date).toLocaleTimeString('es-ES', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </p>
                              </div>
                            </div>

                            {/* Action Button - Analyze only */}
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAnalyzeEvent(event)}
                                disabled={isAnalyzing}
                                className="flex-1 border-zinc-700 h-10 text-xs sm:text-sm"
                              >
                                {isAnalyzing ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  analysis ? 'Ocultar Análisis' : 'Analizar Mercados'
                                )}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Analysis Panel - Mobile optimized */}
                          {analysis && (
                            <div className="border-t border-zinc-800 bg-zinc-900/80 p-3 sm:p-4">
                              {/* ELO + Form + Context Header */}
                              <div className="grid grid-cols-2 gap-3 mb-4">
                                {/* Home Team Info */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-blue-400 mb-1.5">{event.home_team}</h5>
                                  <div className="space-y-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">ELO:</span>
                                      <span className="text-white font-medium">{analysis.elo?.home || '?'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Forma:</span>
                                      <span className="text-white font-mono">{analysis.form?.home?.results || '?????'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Posición:</span>
                                      <span className="text-white">#{analysis.context?.home?.position || '?'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Situación:</span>
                                      <span className={`text-[10px] ${
                                        analysis.context?.home?.situation?.includes('title') ? 'text-yellow-400' :
                                        analysis.context?.home?.situation?.includes('relegation') ? 'text-red-400' :
                                        analysis.context?.home?.situation?.includes('champion') ? 'text-blue-400' :
                                        'text-zinc-400'
                                      }`}>
                                        {analysis.context?.home?.situation?.replace('_', ' ') || '-'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Away Team Info */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-orange-400 mb-1.5">{event.away_team}</h5>
                                  <div className="space-y-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">ELO:</span>
                                      <span className="text-white font-medium">{analysis.elo?.away || '?'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Forma:</span>
                                      <span className="text-white font-mono">{analysis.form?.away?.results || '?????'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Posición:</span>
                                      <span className="text-white">#{analysis.context?.away?.position || '?'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Situación:</span>
                                      <span className={`text-[10px] ${
                                        analysis.context?.away?.situation?.includes('title') ? 'text-yellow-400' :
                                        analysis.context?.away?.situation?.includes('relegation') ? 'text-red-400' :
                                        analysis.context?.away?.situation?.includes('champion') ? 'text-blue-400' :
                                        'text-zinc-400'
                                      }`}>
                                        {analysis.context?.away?.situation?.replace('_', ' ') || '-'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Expected Stats */}
                              <div className="flex gap-2 mb-4 text-xs">
                                <div className="flex-1 bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <span className="text-zinc-500 block">xG Esperados</span>
                                  <span className="text-white font-medium">
                                    {analysis.expected_goals?.home?.toFixed(1) || '?'} - {analysis.expected_goals?.away?.toFixed(1) || '?'}
                                  </span>
                                </div>
                                <div className="flex-1 bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <span className="text-zinc-500 block">Córners Est.</span>
                                  <span className="text-yellow-400 font-medium">
                                    {analysis.corners?.analysis?.expected_total || analysis.expected_corners || '?'}
                                  </span>
                                </div>
                                <div className="flex-1 bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <span className="text-zinc-500 block">Tarjetas Est.</span>
                                  <span className="text-red-400 font-medium">
                                    {analysis.cards?.analysis?.expected_total || analysis.expected_cards || '?'}
                                  </span>
                                </div>
                              </div>

                              {/* NUEVO: Árbitro */}
                              {analysis.referee?.name && (
                                <div className="bg-gradient-to-r from-zinc-800/50 to-zinc-900/50 rounded-lg p-3 mb-4 border border-zinc-700/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">⚖️</span>
                                    <h5 className="text-xs font-semibold text-white">Árbitro: {analysis.referee.name}</h5>
                                    {analysis.referee.tendency && (
                                      <Badge className={`text-[9px] h-4 ${
                                        analysis.referee.tendency === 'strict' ? 'bg-red-500/20 text-red-400' :
                                        analysis.referee.tendency === 'lenient' ? 'bg-green-500/20 text-green-400' :
                                        'bg-zinc-500/20 text-zinc-400'
                                      }`}>
                                        {analysis.referee.tendency === 'strict' ? 'Estricto' : 
                                         analysis.referee.tendency === 'lenient' ? 'Permisivo' : 'Normal'}
                                      </Badge>
                                    )}
                                  </div>
                                  {analysis.referee.cards_per_match > 0 && (
                                    <div className="grid grid-cols-3 gap-2 text-[10px]">
                                      <div className="text-center">
                                        <span className="text-yellow-400 font-bold block">{analysis.referee.avg_yellow_cards || 0}</span>
                                        <span className="text-zinc-500">🟨/partido</span>
                                      </div>
                                      <div className="text-center">
                                        <span className="text-red-400 font-bold block">{analysis.referee.avg_red_cards || 0}</span>
                                        <span className="text-zinc-500">🟥/partido</span>
                                      </div>
                                      <div className="text-center">
                                        <span className="text-white font-bold block">{analysis.referee.total_matches || 0}</span>
                                        <span className="text-zinc-500">partidos</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* NUEVO: Goleadores */}
                              {(analysis.scorers?.home?.length > 0 || analysis.scorers?.away?.length > 0) && (
                                <div className="mb-4">
                                  <h5 className="text-xs font-semibold text-[#00E599] mb-2 flex items-center gap-1">
                                    ⚽ Goleadores (Anytime Scorer)
                                  </h5>
                                  <div className="grid grid-cols-2 gap-2">
                                    {/* Home Scorers */}
                                    <div className="bg-zinc-800/50 rounded-lg p-2">
                                      <span className="text-[10px] text-blue-400 block mb-1">{event.home_team}</span>
                                      {analysis.scorers?.home?.slice(0, 3).map((scorer, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-[10px] py-0.5">
                                          <span className="text-white truncate flex-1">{scorer.player_name}</span>
                                          <div className="flex items-center gap-1">
                                            <span className="text-zinc-400">{scorer.total_goals}g</span>
                                            <span className={`font-bold ${
                                              scorer.value_rating === 'high' ? 'text-green-400' :
                                              scorer.value_rating === 'medium' ? 'text-yellow-400' : 'text-zinc-400'
                                            }`}>
                                              @{scorer.typical_odds?.toFixed(2)}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                      {!analysis.scorers?.home?.length && (
                                        <span className="text-[10px] text-zinc-500">Sin datos</span>
                                      )}
                                    </div>
                                    {/* Away Scorers */}
                                    <div className="bg-zinc-800/50 rounded-lg p-2">
                                      <span className="text-[10px] text-orange-400 block mb-1">{event.away_team}</span>
                                      {analysis.scorers?.away?.slice(0, 3).map((scorer, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-[10px] py-0.5">
                                          <span className="text-white truncate flex-1">{scorer.player_name}</span>
                                          <div className="flex items-center gap-1">
                                            <span className="text-zinc-400">{scorer.total_goals}g</span>
                                            <span className={`font-bold ${
                                              scorer.value_rating === 'high' ? 'text-green-400' :
                                              scorer.value_rating === 'medium' ? 'text-yellow-400' : 'text-zinc-400'
                                            }`}>
                                              @{scorer.typical_odds?.toFixed(2)}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                      {!analysis.scorers?.away?.length && (
                                        <span className="text-[10px] text-zinc-500">Sin datos</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* NUEVO: Lesiones y Suspensiones */}
                              {(analysis.injuries?.home?.players?.length > 0 || analysis.injuries?.away?.players?.length > 0) && (
                                <div className="mb-4">
                                  <h5 className="text-xs font-semibold text-red-400 mb-2 flex items-center gap-1">
                                    🏥 Lesiones y Suspensiones
                                  </h5>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className={`rounded-lg p-2 ${analysis.injuries?.home?.impact?.impact_level === 'high' ? 'bg-red-500/20 border border-red-500/30' : 'bg-zinc-800/50'}`}>
                                      <span className="text-[10px] text-blue-400 block mb-1">{event.home_team}</span>
                                      {analysis.injuries?.home?.players?.slice(0, 3).map((inj, idx) => (
                                        <div key={idx} className="text-[10px] text-zinc-300 py-0.5 truncate">
                                          ❌ {inj.player_name}
                                        </div>
                                      ))}
                                      {analysis.injuries?.home?.impact?.impact_level !== 'none' && (
                                        <Badge className={`mt-1 text-[9px] ${
                                          analysis.injuries?.home?.impact?.impact_level === 'high' ? 'bg-red-500/30 text-red-300' :
                                          analysis.injuries?.home?.impact?.impact_level === 'medium' ? 'bg-yellow-500/30 text-yellow-300' :
                                          'bg-zinc-500/30 text-zinc-300'
                                        }`}>
                                          Impacto: {analysis.injuries?.home?.impact?.impact_level}
                                        </Badge>
                                      )}
                                      {!analysis.injuries?.home?.players?.length && (
                                        <span className="text-[10px] text-green-400">✓ Plantilla completa</span>
                                      )}
                                    </div>
                                    <div className={`rounded-lg p-2 ${analysis.injuries?.away?.impact?.impact_level === 'high' ? 'bg-red-500/20 border border-red-500/30' : 'bg-zinc-800/50'}`}>
                                      <span className="text-[10px] text-orange-400 block mb-1">{event.away_team}</span>
                                      {analysis.injuries?.away?.players?.slice(0, 3).map((inj, idx) => (
                                        <div key={idx} className="text-[10px] text-zinc-300 py-0.5 truncate">
                                          ❌ {inj.player_name}
                                        </div>
                                      ))}
                                      {analysis.injuries?.away?.impact?.impact_level !== 'none' && (
                                        <Badge className={`mt-1 text-[9px] ${
                                          analysis.injuries?.away?.impact?.impact_level === 'high' ? 'bg-red-500/30 text-red-300' :
                                          analysis.injuries?.away?.impact?.impact_level === 'medium' ? 'bg-yellow-500/30 text-yellow-300' :
                                          'bg-zinc-500/30 text-zinc-300'
                                        }`}>
                                          Impacto: {analysis.injuries?.away?.impact?.impact_level}
                                        </Badge>
                                      )}
                                      {!analysis.injuries?.away?.players?.length && (
                                        <span className="text-[10px] text-green-400">✓ Plantilla completa</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* NUEVO: Rendimiento Casa/Fuera */}
                              {(analysis.home_away_performance?.home_team?.home || analysis.home_away_performance?.away_team?.away) && (
                                <div className="mb-4">
                                  <h5 className="text-xs font-semibold text-purple-400 mb-2 flex items-center gap-1">
                                    🏠 Rendimiento Casa/Fuera
                                  </h5>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="bg-zinc-800/50 rounded-lg p-2">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] text-blue-400">{event.home_team} (Local)</span>
                                        {analysis.home_away_performance?.home_team?.home?.strength && (
                                          <Badge className={`text-[9px] ${
                                            analysis.home_away_performance.home_team.home.strength === 'strong' ? 'bg-green-500/20 text-green-400' :
                                            analysis.home_away_performance.home_team.home.strength === 'weak' ? 'bg-red-500/20 text-red-400' :
                                            'bg-zinc-500/20 text-zinc-400'
                                          }`}>
                                            {analysis.home_away_performance.home_team.home.strength === 'strong' ? 'Fuerte' :
                                             analysis.home_away_performance.home_team.home.strength === 'weak' ? 'Débil' : 'Normal'}
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="text-[10px] text-zinc-300">
                                        <div>Victorias: {analysis.home_away_performance?.home_team?.home?.win_pct || 0}%</div>
                                        <div>PPG: {analysis.home_away_performance?.home_team?.home?.ppg || 0}</div>
                                        <div>Goles/P: {analysis.home_away_performance?.home_team?.home?.goals_per_game || 0}</div>
                                      </div>
                                    </div>
                                    <div className="bg-zinc-800/50 rounded-lg p-2">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] text-orange-400">{event.away_team} (Fuera)</span>
                                        {analysis.home_away_performance?.away_team?.away?.strength && (
                                          <Badge className={`text-[9px] ${
                                            analysis.home_away_performance.away_team.away.strength === 'strong' ? 'bg-green-500/20 text-green-400' :
                                            analysis.home_away_performance.away_team.away.strength === 'weak' ? 'bg-red-500/20 text-red-400' :
                                            'bg-zinc-500/20 text-zinc-400'
                                          }`}>
                                            {analysis.home_away_performance.away_team.away.strength === 'strong' ? 'Fuerte' :
                                             analysis.home_away_performance.away_team.away.strength === 'weak' ? 'Débil' : 'Normal'}
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="text-[10px] text-zinc-300">
                                        <div>Victorias: {analysis.home_away_performance?.away_team?.away?.win_pct || 0}%</div>
                                        <div>PPG: {analysis.home_away_performance?.away_team?.away?.ppg || 0}</div>
                                        <div>Goles/P: {analysis.home_away_performance?.away_team?.away?.goals_per_game || 0}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* NUEVO: Congestión de Partidos */}
                              {(analysis.fixture_congestion?.home?.has_european || analysis.fixture_congestion?.away?.has_european) && (
                                <div className="mb-4">
                                  <h5 className="text-xs font-semibold text-yellow-400 mb-2 flex items-center gap-1">
                                    📅 Congestión de Partidos
                                  </h5>
                                  <div className="grid grid-cols-2 gap-2">
                                    {analysis.fixture_congestion?.home?.has_european && (
                                      <div className={`rounded-lg p-2 ${analysis.fixture_congestion.home.rotation_risk === 'high' ? 'bg-yellow-500/20 border border-yellow-500/30' : 'bg-zinc-800/50'}`}>
                                        <span className="text-[10px] text-blue-400 block">{event.home_team}</span>
                                        <span className="text-[10px] text-yellow-400">⚽ Europa en {analysis.fixture_congestion.home.days_to_european} días</span>
                                        <Badge className={`ml-1 text-[9px] ${
                                          analysis.fixture_congestion.home.rotation_risk === 'high' ? 'bg-red-500/20 text-red-300' : 'bg-zinc-500/20 text-zinc-300'
                                        }`}>
                                          Rotación: {analysis.fixture_congestion.home.rotation_risk}
                                        </Badge>
                                      </div>
                                    )}
                                    {analysis.fixture_congestion?.away?.has_european && (
                                      <div className={`rounded-lg p-2 ${analysis.fixture_congestion.away.rotation_risk === 'high' ? 'bg-yellow-500/20 border border-yellow-500/30' : 'bg-zinc-800/50'}`}>
                                        <span className="text-[10px] text-orange-400 block">{event.away_team}</span>
                                        <span className="text-[10px] text-yellow-400">⚽ Europa en {analysis.fixture_congestion.away.days_to_european} días</span>
                                        <Badge className={`ml-1 text-[9px] ${
                                          analysis.fixture_congestion.away.rotation_risk === 'high' ? 'bg-red-500/20 text-red-300' : 'bg-zinc-500/20 text-zinc-300'
                                        }`}>
                                          Rotación: {analysis.fixture_congestion.away.rotation_risk}
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* NUEVO: H2H Detallado */}
                              {analysis.h2h?.total_matches > 0 && (
                                <div className="mb-4">
                                  <h5 className="text-xs font-semibold text-cyan-400 mb-2 flex items-center gap-1">
                                    📊 Historial H2H ({analysis.h2h.total_matches} partidos)
                                  </h5>
                                  <div className="bg-zinc-800/50 rounded-lg p-2">
                                    <div className="grid grid-cols-4 gap-2 text-center text-[10px] mb-2">
                                      <div>
                                        <span className="text-blue-400 font-bold block">{analysis.h2h.home_wins}</span>
                                        <span className="text-zinc-500">Loc.</span>
                                      </div>
                                      <div>
                                        <span className="text-zinc-400 font-bold block">{analysis.h2h.draws}</span>
                                        <span className="text-zinc-500">Emp.</span>
                                      </div>
                                      <div>
                                        <span className="text-orange-400 font-bold block">{analysis.h2h.away_wins}</span>
                                        <span className="text-zinc-500">Vis.</span>
                                      </div>
                                      <div>
                                        <span className="text-white font-bold block">{analysis.h2h.avg_goals}</span>
                                        <span className="text-zinc-500">Goles/P</span>
                                      </div>
                                    </div>
                                    <div className="flex gap-2 text-[9px]">
                                      <Badge className={`${analysis.h2h.over_25_pct > 60 ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                                        Over 2.5: {analysis.h2h.over_25_pct}%
                                      </Badge>
                                      <Badge className={`${analysis.h2h.btts_pct > 60 ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                                        BTTS: {analysis.h2h.btts_pct}%
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* NUEVO: Formaciones */}
                              {analysis.formations?.matchup && (
                                <div className="mb-4">
                                  <h5 className="text-xs font-semibold text-indigo-400 mb-2 flex items-center gap-1">
                                    📐 Formaciones
                                  </h5>
                                  <div className="bg-zinc-800/50 rounded-lg p-2">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-center flex-1">
                                        <span className="text-white font-bold text-sm block">{analysis.formations?.home?.formation || '?'}</span>
                                        <Badge className={`text-[9px] ${
                                          analysis.formations?.home?.style === 'offensive' ? 'bg-green-500/20 text-green-400' :
                                          analysis.formations?.home?.style === 'defensive' ? 'bg-red-500/20 text-red-400' :
                                          'bg-zinc-500/20 text-zinc-400'
                                        }`}>
                                          {analysis.formations?.home?.style === 'offensive' ? 'Ofensivo' :
                                           analysis.formations?.home?.style === 'defensive' ? 'Defensivo' : 'Equilibrado'}
                                        </Badge>
                                      </div>
                                      <span className="text-zinc-500 text-xs px-2">vs</span>
                                      <div className="text-center flex-1">
                                        <span className="text-white font-bold text-sm block">{analysis.formations?.away?.formation || '?'}</span>
                                        <Badge className={`text-[9px] ${
                                          analysis.formations?.away?.style === 'offensive' ? 'bg-green-500/20 text-green-400' :
                                          analysis.formations?.away?.style === 'defensive' ? 'bg-red-500/20 text-red-400' :
                                          'bg-zinc-500/20 text-zinc-400'
                                        }`}>
                                          {analysis.formations?.away?.style === 'offensive' ? 'Ofensivo' :
                                           analysis.formations?.away?.style === 'defensive' ? 'Defensivo' : 'Equilibrado'}
                                        </Badge>
                                      </div>
                                    </div>
                                    <p className="text-[10px] text-zinc-400 text-center">
                                      {analysis.formations?.matchup?.description}
                                    </p>
                                  </div>
                                </div>
                              )}

                              <h4 className="text-xs font-semibold text-[#00E599] mb-3">
                                Probabilidades por Mercado
                              </h4>
                              
                              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                {/* 1X2 */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-zinc-400 mb-1.5">1X2</h5>
                                  <div className="space-y-1">
                                    <ProgressBar value={analysis.match_1x2?.home || 0} label="L" color="bg-blue-500" />
                                    <ProgressBar value={analysis.match_1x2?.draw || 0} label="E" color="bg-zinc-500" />
                                    <ProgressBar value={analysis.match_1x2?.away || 0} label="V" color="bg-orange-500" />
                                  </div>
                                </div>
                                
                                {/* Double Chance */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-zinc-400 mb-1.5">Doble Oport.</h5>
                                  <div className="space-y-1">
                                    <ProgressBar value={analysis.double_chance?.["1x"] || 0} label="1X" color="bg-blue-400" />
                                    <ProgressBar value={analysis.double_chance?.x2 || 0} label="X2" color="bg-orange-400" />
                                    <ProgressBar value={analysis.double_chance?.["12"] || 0} label="12" color="bg-purple-400" />
                                  </div>
                                </div>
                                
                                {/* Over/Under 2.5 */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-zinc-400 mb-1.5">O/U 2.5 Goles</h5>
                                  <div className="space-y-1">
                                    <ProgressBar value={analysis.over_under_25?.over || 0} label="O" color="bg-green-500" />
                                    <ProgressBar value={analysis.over_under_25?.under || 0} label="U" color="bg-red-500" />
                                  </div>
                                </div>
                                
                                {/* BTTS */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-zinc-400 mb-1.5">BTTS</h5>
                                  <div className="space-y-1">
                                    <ProgressBar value={analysis.btts?.yes || 0} label="Sí" color="bg-green-500" />
                                    <ProgressBar value={analysis.btts?.no || 0} label="No" color="bg-red-500" />
                                  </div>
                                </div>

                                {/* Handicap */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-zinc-400 mb-1.5">Hándicap</h5>
                                  <div className="space-y-1">
                                    <ProgressBar value={analysis.handicap?.home_minus1 || 0} label="-1" color="bg-blue-500" />
                                    <ProgressBar value={analysis.handicap?.away_plus1 || 0} label="+1" color="bg-orange-500" />
                                  </div>
                                </div>

                                {/* Team Goals */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-zinc-400 mb-1.5">Goles Equipo</h5>
                                  <div className="space-y-1">
                                    <ProgressBar value={analysis.team_goals?.home_over_05 || 0} label="L+0.5" color="bg-blue-400" />
                                    <ProgressBar value={analysis.team_goals?.away_over_05 || 0} label="V+0.5" color="bg-orange-400" />
                                  </div>
                                </div>
                                
                                {/* Corners */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-zinc-400 mb-1.5">Córners 9.5</h5>
                                  <div className="space-y-1">
                                    <ProgressBar value={analysis.corners_95?.over || 0} label="O" color="bg-yellow-500" />
                                    <ProgressBar value={analysis.corners_95?.under || 0} label="U" color="bg-yellow-700" />
                                  </div>
                                </div>

                                {/* Cards */}
                                <div className="bg-zinc-800/50 rounded-lg p-2.5">
                                  <h5 className="text-[10px] font-medium text-zinc-400 mb-1.5">Tarjetas 3.5</h5>
                                  <div className="space-y-1">
                                    <ProgressBar value={analysis.cards_35?.over || 0} label="O" color="bg-red-500" />
                                    <ProgressBar value={analysis.cards_35?.under || 0} label="U" color="bg-red-700" />
                                  </div>
                                </div>
                              </div>
                              
                              {/* Analysis Summary */}
                              {analysis.analysis_summary && (
                                <p className="mt-3 text-xs text-zinc-400 bg-zinc-800/30 rounded p-2">
                                  {analysis.analysis_summary}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Counter */}
          {!loading && filteredEvents.length > 0 && (
            <div className="mt-6 text-center text-xs text-zinc-500">
              {filteredEvents.length} eventos
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
